import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as ColumnIcon } from '@elementary/common-ui/assets/column.svg';
import { ReactComponent as TableIcon } from '@elementary/common-ui/assets/table.svg';
import { ReactComponent as TestIcon } from '@elementary/common-ui/assets/test-icon-small.svg';
import { Flex, FlexColumn } from '@elementary/common-ui/components';
import useHover from '@elementary/common-ui/hooks/useHover';
import { capitalize } from '@elementary/common-ui/utils/common';
import { Typography } from '@mui/material';
import { UseFormWatch } from 'react-hook-form';
import { AddTestForm, ColumnResource, Resource } from 'shared/types';
import { WizardResourceType } from 'testManagement/types';
import { getWizardStages } from 'testManagement/utils/utils';
import { fontSize, fontWeight, space } from '@elementary/common-ui/style/utils';
import StepperTargetPopup from './StepperTargetPopup';

export const StepsWrapper = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.palette.border.subtle};
`;

const Step = styled(Flex)<{ $selected?: boolean; $disabled?: boolean }>`
  position: relative;
  width: 100%;
  gap: ${space('xxl')};
  padding: ${space(0, 'xxl')};
  height: 72px;
  background: ${({ theme, $selected }) =>
    $selected ? theme.palette.surface.tinted : theme.palette.surface.base};
  font-weight: ${({ $selected, theme }) =>
    $selected ? theme.typography.fontWeightBold : fontWeight('regular')};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  :hover {
    background: ${({ theme, $disabled }) =>
      !$disabled && theme.palette.surface.muted};
  }
`;

const StepLeft = styled(Flex)`
  gap: ${space('xxl')};
  align-items: center;
`;

const StepText = styled(Typography)<{
  $selected?: boolean;
  $stepOver?: boolean;
}>`
  font-weight: inherit;
  white-space: nowrap;
  color: ${({ theme, $selected, $stepOver }) =>
    $selected
      ? theme.palette.accent.secondary
      : $stepOver
      ? theme.palette.content.primary
      : theme.palette.content.subtle};
`;

const StepSubText = styled(Typography)`
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.content.muted};
`;

const StepDetail = styled(Flex)`
  gap: ${space('sm')};
  align-items: center;
`;

const StepIndex = styled(StepText)<{
  $selected?: boolean;
  $stepOver?: boolean;
}>`
  font-size: ${fontSize('xxxl')};
  color: ${({ theme, $selected }) =>
    $selected ? theme.palette.accent.secondary : theme.palette.content.subtle};
`;

const Divider = styled.div`
  background: ${({ theme }) => theme.palette.surface.hover};
  height: 100%;
  width: 1px;
  min-width: 1px;
`;

const testTypeNameMap: {
  [key in AddTestForm['testType']]: string;
} = {
  freshnessAnomalies: 'Freshness anomalies',
  cloudFreshnessAnomalies: 'Freshness anomalies',
  eventFreshnessAnomalies: 'Event freshness anomalies',
  volumeAnomalies: 'Volume anomalies',
  cloudVolumeAnomalies: 'Volume anomalies',
  dimensionAnomalies: 'Dimension anomalies',
  schemaChanges: 'Schema changes',
  cloudDataContract: 'Data contract',
  cloudPipelineTaskPerformance: 'Pipeline task performance',
  cloudSqlUniqueness: 'Uniqueness',
  cloudSqlCompleteness: 'Completeness',
  cloudSqlValidity: 'Validity',
  cloudSqlRelationship: 'Relationship',
  unique: 'Unique',
  notNull: 'Not null',
  acceptedValues: 'Accepted values',
  relationship: 'Relationship',
  customQuery: 'Custom Query',
  yamlTest: 'Generic YAML Test',
  '': '',
};

type StepBoxProps = {
  stageIndex: number;
  curStage: number;
  title: string;
  icon?: JSX.Element;
  showDescription?: boolean;
  description?: string;
  renderPopup?: () => JSX.Element;
  disabled?: boolean;
  onClick: () => void;
};

export const StepBox = ({
  stageIndex,
  curStage,
  title,
  icon,
  showDescription,
  description,
  renderPopup,
  disabled,
  onClick,
}: StepBoxProps) => {
  const ref = useRef<any>();
  const isHover = useHover(ref);

  return (
    <Step ref={ref} $selected={stageIndex === curStage} $disabled={disabled}>
      <Flex
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
        onClick={disabled ? undefined : onClick}
      >
        <StepLeft>
          <StepIndex
            $selected={stageIndex === curStage}
            $stepOver={stageIndex < curStage}
          >
            {stageIndex}
          </StepIndex>
          <FlexColumn>
            <StepText
              $selected={stageIndex === curStage}
              $stepOver={stageIndex < curStage}
            >
              {title}
            </StepText>
            {showDescription && (
              <StepDetail>
                {icon && React.cloneElement(icon)}
                <StepSubText noWrap>{description}</StepSubText>
              </StepDetail>
            )}
          </FlexColumn>
        </StepLeft>
      </Flex>

      {isHover && renderPopup?.()}
    </Step>
  );
};

type Props = {
  mode: WizardResourceType;
  stage: number;
  watch: UseFormWatch<AddTestForm>;
  onStageClick: (index: number) => void;
  tableResourcesData: Resource[];
  columnsResourcesData: ColumnResource[];
  editMode?: boolean;
};

const TestDialogStepper = ({
  mode,
  stage,
  watch,
  onStageClick,
  tableResourcesData,
  columnsResourcesData,
  editMode,
}: Props) => {
  const [previouslyVisitedSteps, setPreviouslyVisitedSteps] = useState(
    new Set<number>(),
  );

  useEffect(() => {
    setPreviouslyVisitedSteps(new Set(previouslyVisitedSteps.add(stage)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const formatYamlTemplateName = (val: string) =>
    capitalize(val.split('.')[1]?.replaceAll('_', ' '));

  const testDescription =
    watch('testType') === 'yamlTest'
      ? formatYamlTemplateName(watch('yamlTemplateName') || '')
      : testTypeNameMap[watch('testType')];

  const stages = getWizardStages({
    wizardMode: editMode ? 'edit' : 'add',
    wizardResourceType: mode,
  });
  const isMultiStep = stages.length > 1;

  return (
    <StepsWrapper divider={<Divider />}>
      {stages.map((wizardStage) => {
        const stageIndex = wizardStage.index;
        const isSelectTarget = isMultiStep && stageIndex === 1;
        const isSelectTest = isMultiStep && stageIndex === 2;
        const isConfig = isMultiStep && stageIndex === 3;

        return (
          <StepBox
            key={stageIndex}
            stageIndex={stageIndex}
            curStage={stage}
            title={wizardStage.name}
            icon={
              isSelectTarget ? (
                watch('resourceType') === 'tables' ? (
                  <TableIcon />
                ) : (
                  <ColumnIcon />
                )
              ) : isSelectTest ? (
                <TestIcon />
              ) : undefined
            }
            showDescription={
              isSelectTarget
                ? watch('resources')?.length > 0
                : isSelectTest
                ? !!watch('testType')
                : undefined
            }
            description={
              isSelectTarget
                ? `${watch('resources')?.length} ${capitalize(
                    watch('resourceType'),
                  )} selected`
                : isSelectTest
                ? testDescription
                : undefined
            }
            renderPopup={
              isSelectTarget && stage > 1
                ? () => (
                    <StepperTargetPopup
                      watch={watch}
                      tableResourcesData={tableResourcesData}
                      columnsResourcesData={columnsResourcesData}
                    />
                  )
                : undefined
            }
            disabled={
              isSelectTest ? stage <= 2 : isConfig ? stage <= 3 : undefined
            }
            onClick={() => onStageClick(stageIndex)}
          />
        );
      })}
    </StepsWrapper>
  );
};

export default TestDialogStepper;
